// src/components/InteractiveMap.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  ZoomControl,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { regions } from '../data/regions';
import {
  FaSearch,
  FaTree,
  FaCity,
  FaWater,
  FaArrowRight,
  FaMapMarkerAlt,
  FaArrowLeft,
  FaHome,
  FaLocationArrow
} from 'react-icons/fa';

// ==============================
// FIX BULLETPROOF DES ICÔNES LEAFLET
// ==============================
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Création de l'étiquette de prix dynamique (Style Airbnb)
const createPriceIcon = (price) => L.divIcon({
  className: 'custom-price-marker',
  html: `<div style="background-color: white; border: 2px solid #0f172a; border-radius: 20px; padding: 6px 10px; font-weight: 900; font-size: 14px; color: #0f172a; box-shadow: 0 4px 10px rgba(0,0,0,0.15); white-space: nowrap; transition: transform 0.2s ease;" onmouseover="this.style.transform='scale(1.1)'" onmouseout="this.style.transform='scale(1)'">
          ${price} $
         </div>`,
  iconSize: [60, 30],
  iconAnchor: [30, 15],
});

// ==============================
// HELPERS LEAFLET
// ==============================
function FlyToLocation({ target }) {
  const map = useMap();

  useEffect(() => {
    if (target?.centre?.lat != null && target?.centre?.lng != null) {
      map.flyTo([target.centre.lat, target.centre.lng], 8, {
        duration: 1.5,
      });
    }
  }, [target, map]);

  return null;
}

function InvalidateMapSize({ deps = [] }) {
  const map = useMap();

  useEffect(() => {
    const refresh = () => {
      setTimeout(() => {
        map.invalidateSize();
      }, 180);
    };

    refresh();
    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [map, ...deps]);

  return null;
}

const NAV_HEIGHT = 72;

const setMobileViewportUnit = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--forfeo-map-vh', `${vh}px`);
};

function MobileMapSizer({ deps = [] }) {
  const map = useMap();

  useEffect(() => {
    let frameId;
    const refresh = () => {
      setMobileViewportUnit();
      window.clearTimeout(frameId);
      frameId = window.setTimeout(() => {
        try {
          map.invalidateSize(true);
        } catch (error) {
          console.warn('[Forfeo Map] invalidateSize skipped', error);
        }
      }, 120);
    };

    refresh();
    const timers = [80, 240, 600, 1200].map((delay) => window.setTimeout(refresh, delay));

    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);

    return () => {
      window.clearTimeout(frameId);
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [map, ...deps]);

  return null;
}

const InteractiveMap = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // all | ville | nature | maritime
  const [selectedRegion, setSelectedRegion] = useState(null);

  useEffect(() => {
    setMobileViewportUnit();
    const updateViewport = () => setMobileViewportUnit();
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);
    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
    };
  }, []);

  // État pour stocker les logements récupérés depuis Supabase
  const [listings, setListings] = useState([]);

  // ✅ OPTIMISATION BDD : Récupération instantanée sans boucle de géocodage
  useEffect(() => {
    const fetchListings = async () => {
      const { data, error } = await supabase.from('habitat_listings').select('*').eq('status', 'published');
      if (data && !error) {
        // On ne garde que les logements qui ont bien leurs coordonnées dans la base de données
        const validListings = data.filter(listing => listing.lat && listing.lng);
        setListings(validListings);
      }
    };
    fetchListings();
  }, []);

  // ==============================
  // FILTRAGE DES RÉGIONS
  // ==============================
  const filteredRegions = useMemo(() => {
    return regions.filter((region) => {
      const nom = String(region.nom || '').toLowerCase();
      const desc = String(region.description || '').toLowerCase();
      const query = searchTerm.trim().toLowerCase();

      const matchesSearch =
        !query ||
        nom.includes(query) ||
        desc.includes(query) ||
        (region.villes || []).some((ville) =>
          String(ville).toLowerCase().includes(query)
        );

      let matchesFilter = true;

      if (activeFilter === 'ville') {
        matchesFilter = [
          'montréal', 'montreal', 'québec', 'quebec', 'laval', 'gatineau',
        ].some((v) => nom.includes(v));
      }

      if (activeFilter === 'maritime') {
        matchesFilter = [
          'gaspésie', 'gaspesie', 'bas-saint-laurent', 'côte-nord', 'cote-nord', 'îles', 'iles',
        ].some((v) => nom.includes(v));
      }

      if (activeFilter === 'nature') {
        matchesFilter = ![
          'montréal', 'montreal', 'québec', 'quebec', 'laval', 'gatineau',
        ].some((v) => nom.includes(v));
      }

      return matchesSearch && matchesFilter;
    });
  }, [searchTerm, activeFilter]);

  useEffect(() => {
    if (
      selectedRegion &&
      !filteredRegions.some((r) => r.id === selectedRegion.id)
    ) {
      setSelectedRegion(null);
    }
  }, [filteredRegions, selectedRegion]);

  useEffect(() => {
    if (!selectedRegion && filteredRegions.length > 0) {
      setSelectedRegion(filteredRegions[0]);
    }
  }, [filteredRegions, selectedRegion]);

  return (
    <div
      className="forfeo-interactive-map-page bg-slate-50 w-full relative overflow-hidden"
      style={{
        minHeight: `calc((var(--forfeo-map-vh, 1vh) * 100) - ${NAV_HEIGHT}px)`,
        height: `calc((var(--forfeo-map-vh, 1vh) * 100) - ${NAV_HEIGHT}px)`,
      }}
    >
      <style>{`
        .forfeo-interactive-map-page .leaflet-container {
          width: 100% !important;
          height: 100% !important;
          min-height: 100% !important;
          background: #e2e8f0;
          touch-action: pan-x pan-y;
        }
        .forfeo-interactive-map-page .leaflet-control-container {
          font-family: inherit;
        }
        @media (max-width: 767px) {
          .forfeo-interactive-map-page {
            min-height: calc((var(--forfeo-map-vh, 1vh) * 100) - 64px) !important;
            height: calc((var(--forfeo-map-vh, 1vh) * 100) - 64px) !important;
            max-height: calc((var(--forfeo-map-vh, 1vh) * 100) - 64px) !important;
            overflow: hidden !important;
          }
          .forfeo-interactive-map-page .leaflet-bottom.leaflet-right {
            bottom: calc(25vh + env(safe-area-inset-bottom, 0px) + 24px) !important;
            right: 10px !important;
          }
          .forfeo-interactive-map-page .leaflet-control-zoom {
            border: none !important;
            box-shadow: 0 14px 30px rgba(15, 23, 42, 0.18) !important;
          }
          .forfeo-interactive-map-page .leaflet-control-zoom a {
            width: 34px;
            height: 34px;
            line-height: 34px;
            font-size: 18px;
          }
          .forfeo-interactive-map-page .leaflet-popup-content-wrapper {
            border-radius: 18px;
          }
          .forfeo-mobile-map-scroll::-webkit-scrollbar {
            display: none;
          }
          .forfeo-mobile-map-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        }
      `}</style>
      <div className="flex flex-col md:flex-row w-full h-full min-h-0">
        {/* Sidebar desktop */}
        <aside className="hidden md:flex md:w-80 lg:w-96 md:flex-col bg-white border-r border-slate-200 shadow-xl h-full relative z-10">
          <div className="p-6 text-white" style={{ backgroundColor: '#0f172a' }}>
            <h2 className="text-xl font-bold mb-2">Explorateur</h2>
            <p className="text-xs text-blue-200 mb-4">
              Découvrez les 17 régions du Québec
            </p>

            <div className="relative">
              <input
                type="text"
                placeholder="Chercher une région ou une ville..."
                className="w-full pl-10 pr-4 py-3 rounded-xl text-slate-800 text-sm focus:outline-none shadow-inner"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 bg-slate-50">
            {filteredRegions.length === 0 ? (
              <div className="p-4 rounded-2xl bg-white border border-slate-200 text-sm text-slate-500">
                Aucune région trouvée.
              </div>
            ) : (
              filteredRegions.map((region) => (
                <div
                  key={region.id}
                  onClick={() => setSelectedRegion(region)}
                  className={`p-4 rounded-2xl cursor-pointer transition-all mb-3 border ${
                    selectedRegion?.id === region.id
                      ? 'bg-white border-blue-500 shadow-md ring-1 ring-blue-500'
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4
                      className={`font-bold text-sm ${
                        selectedRegion?.id === region.id
                          ? 'text-blue-700'
                          : 'text-slate-800'
                      }`}
                    >
                      {region.nom}
                    </h4>
                    <FaMapMarkerAlt
                      className={`text-xs shrink-0 ${
                        selectedRegion?.id === region.id
                          ? 'text-blue-500'
                          : 'text-slate-300'
                      }`}
                    />
                  </div>

                  <p className="text-xs text-slate-500 leading-relaxed mb-3 line-clamp-3">
                    {region.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {region.villes?.slice(0, 3).map((ville) => (
                      <span
                        key={ville}
                        className="px-2 py-1 rounded-full bg-slate-100 text-[10px] font-bold text-slate-600"
                      >
                        {ville}
                      </span>
                    ))}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/region/${region.id}`);
                    }}
                    className="w-full bg-slate-100 hover:bg-blue-600 hover:text-white text-blue-600 border border-slate-200 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 group"
                  >
                    Explorer la région
                    <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Carte */}
        <section className="relative flex-1 h-full min-h-0 w-full overflow-hidden">
          <div className="relative w-full h-full min-h-0">
            {/* Filtres flottants */}
            <div className="absolute top-3 left-3 right-3 md:top-4 md:left-1/2 md:right-auto md:-translate-x-1/2 z-[500] md:w-auto md:max-w-[760px] overflow-x-auto forfeo-mobile-map-scroll">
              <div className="bg-white/95 backdrop-blur-xl p-1 rounded-full shadow-lg flex gap-1 border border-slate-100 min-w-max mx-auto md:p-1.5">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-4 py-2 rounded-full text-[11px] md:px-5 md:text-xs font-bold transition whitespace-nowrap ${
                    activeFilter === 'all'
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Tout
                </button>

                <button
                  onClick={() => setActiveFilter('ville')}
                  className={`px-4 py-2 rounded-full text-[11px] md:px-5 md:text-xs font-bold transition flex items-center gap-1.5 md:gap-2 whitespace-nowrap ${
                    activeFilter === 'ville'
                      ? 'bg-blue-500 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <FaCity /> Villes
                </button>

                <button
                  onClick={() => setActiveFilter('nature')}
                  className={`px-4 py-2 rounded-full text-[11px] md:px-5 md:text-xs font-bold transition flex items-center gap-1.5 md:gap-2 whitespace-nowrap ${
                    activeFilter === 'nature'
                      ? 'bg-green-500 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <FaTree /> Nature
                </button>

                <button
                  onClick={() => setActiveFilter('maritime')}
                  className={`px-4 py-2 rounded-full text-[11px] md:px-5 md:text-xs font-bold transition flex items-center gap-1.5 md:gap-2 whitespace-nowrap ${
                    activeFilter === 'maritime'
                      ? 'bg-cyan-500 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <FaWater /> Mer
                </button>
              </div>
            </div>

            {/* Recherche mobile */}
            <div className="absolute top-[62px] left-3 right-3 z-[500] md:hidden">
              <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200">
                <input
                  type="text"
                  placeholder="Chercher une région ou ville..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-2xl text-slate-800 text-[13px] focus:outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              </div>
            </div>

            {/* Carte Leaflet */}
            <MapContainer
              center={[52, -70]}
              zoom={5}
              scrollWheelZoom={true}
              touchZoom={true}
              dragging={true}
              tap={true}
              doubleClickZoom={true}
              zoomControl={false}
              preferCanvas={true}
              className="forfeo-leaflet-map w-full h-full z-0"
              style={{ width: '100%', height: '100%', minHeight: '100%' }}
              whenCreated={(map) => {
                window.setTimeout(() => map.invalidateSize(true), 100);
                window.setTimeout(() => map.invalidateSize(true), 450);
              }}
            >
              <ZoomControl position="bottomright" />

              <InvalidateMapSize
                deps={[
                  filteredRegions.length,
                  selectedRegion?.id,
                  activeFilter,
                  searchTerm,
                ]}
              />
              <MobileMapSizer
                deps={[
                  filteredRegions.length,
                  selectedRegion?.id,
                  activeFilter,
                  searchTerm,
                ]}
              />

              <TileLayer
                attribution="© OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {selectedRegion && <FlyToLocation target={selectedRegion} />}

              {/* 1. AFFICHAGE DES RÉGIONS */}
              {filteredRegions.map((region) => {
                const lat = region?.centre?.lat;
                const lng = region?.centre?.lng;

                if (typeof lat !== 'number' || typeof lng !== 'number') {
                  return null;
                }

                const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(region.nom + ', Québec, Canada')}`;

                return (
                  <Marker
                    key={`region-${region.id}`}
                    position={[lat, lng]}
                    eventHandlers={{
                      click: () => setSelectedRegion(region),
                    }}
                  >
                    <Popup className="custom-popup">
                      <div className="text-center min-w-[210px] p-1">
                        <h3 className="font-bold text-lg text-slate-900 mb-1">
                          {region.nom}
                        </h3>

                        <p className="text-xs text-slate-500 mb-3 line-clamp-3">
                          {region.description}
                        </p>

                        <div className="flex flex-wrap justify-center gap-1.5 mb-3">
                          {region.villes?.slice(0, 3).map((ville) => (
                            <span
                              key={ville}
                              className="px-2 py-1 rounded-full bg-slate-100 text-[10px] font-bold text-slate-600"
                            >
                              {ville}
                            </span>
                          ))}
                        </div>

                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="w-full bg-slate-900 text-white py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 transition flex items-center justify-center gap-2 shadow-lg mb-2"
                        >
                           <FaLocationArrow /> S'y rendre
                        </a>

                        <button
                          onClick={() => navigate(`/region/${region.id}`)}
                          className="w-full bg-blue-600 text-white py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition flex items-center justify-center gap-2 shadow-md"
                        >
                          Explorer <FaArrowRight />
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {/* 2. AFFICHAGE DES LOGEMENTS AVEC ÉTIQUETTES DE PRIX ET ITINÉRAIRE */}
              {listings.map((listing) => {
                // ✅ NOUVEAU: GÉNÉRATION DE L'URL GOOGLE MAPS POUR L'ITINÉRAIRE
                const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${listing.lat},${listing.lng}`;
                
                return (
                  <Marker 
                    key={`listing-${listing.id}`} 
                    position={[listing.lat, listing.lng]}
                    icon={createPriceIcon(listing.monthly_rent)}
                  >
                    <Popup className="font-sans min-w-[220px]">
                      <div className="text-center">
                        <div className="relative h-24 mb-3 overflow-hidden rounded-lg">
                          <img src={listing.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1de2d9d000'} alt="Logement" className="w-full h-full object-cover" />
                          <div className="absolute top-2 left-2 bg-white/90 px-2 py-1 rounded text-[10px] font-black">{listing.bedrooms} ch.</div>
                        </div>
                        <h4 className="font-bold text-slate-900 text-sm mb-1 truncate">{listing.title}</h4>
                        <p className="text-xs text-slate-500 mb-3"><FaMapMarkerAlt className="inline mr-1" />{listing.city}</p>
                        
                        {/* ✅ NOUVEAU BOUTON : OUVRE GOOGLE MAPS */}
                        <a 
                          href={directionsUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="w-full bg-slate-100 text-slate-700 py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition flex items-center justify-center gap-2 mb-2"
                        >
                          <FaLocationArrow /> Itinéraire
                        </a>

                        <button 
                          onClick={() => navigate(`/habitat/logement/${listing.id}`)}
                          className="w-full bg-teal-500 text-white py-2 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-teal-600 transition"
                        >
                          Voir le logement
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

            </MapContainer>

            {/* Liste mobile compacte : le bouton Explorer est intégré au panneau pour ne plus cacher la carte */}
            <div className="md:hidden absolute left-3 right-3 z-[500] bottom-[calc(env(safe-area-inset-bottom,0px)+10px)]">
              <div className="bg-white/96 backdrop-blur-xl rounded-2xl shadow-xl border border-slate-200 overflow-hidden max-h-[25vh] flex flex-col">
                <div className="px-3 pt-2 pb-1 shrink-0">
                  <div className="mx-auto mb-2 h-1 w-12 rounded-full bg-slate-300" />

                  {selectedRegion && (
                    <div className="mb-2 rounded-2xl bg-slate-950 text-white shadow-lg border border-white/10 p-2.5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[8px] font-black uppercase tracking-[0.16em] text-blue-200">Région sélectionnée</div>
                          <div className="font-black text-[13px] truncate">{selectedRegion.nom}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigate(`/region/${selectedRegion.id}`)}
                          className="shrink-0 rounded-xl bg-blue-600 px-3.5 py-2 text-[9px] font-black uppercase tracking-widest text-white shadow-lg active:scale-95"
                        >
                          Explorer
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Régions disponibles
                  </div>
                </div>

                <div className="px-3 pb-3 overflow-y-auto overscroll-contain forfeo-mobile-map-scroll">
                  {filteredRegions.length === 0 ? (
                    <div className="text-sm text-slate-500 py-2">
                      Aucune région trouvée.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {filteredRegions.slice(0, 8).map((region) => (
                        <button
                          key={region.id}
                          onClick={() => setSelectedRegion(region)}
                          className={`w-full text-left p-2.5 rounded-xl border transition ${
                            selectedRegion?.id === region.id
                              ? 'bg-blue-50 border-blue-300'
                              : 'bg-white border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-bold text-[13px] text-slate-800 truncate">
                                {region.nom}
                              </div>
                              <div className="text-[11px] text-slate-500 truncate">
                                {region.description}
                              </div>
                            </div>
                            <FaMapMarkerAlt
                              className={`shrink-0 ${
                                selectedRegion?.id === region.id
                                  ? 'text-blue-600'
                                  : 'text-slate-300'
                              }`}
                            />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InteractiveMap;
